import { Head, Link, router, useForm, usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Group,
  Menu,
  NativeSelect,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core'
import { ArrowDownWideNarrow, CheckCircle2, CircleDot, MessageSquare, Search } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { IssueTypeBadge } from '@/components/badges'
import { timeAgo } from '@/lib/session-utils'
import type { IssueIndexPageProps, IssueSummaryProps } from '@/types'

function IssueStateIcon({ state }: { state: IssueSummaryProps['state'] }) {
  if (state === 'closed') {
    return <CheckCircle2 size={16} color="var(--mantine-color-violet-6)" />
  }
  return <CircleDot size={16} color="var(--mantine-color-teal-6)" />
}

function IssueRow({
  projectId,
  issue,
  selected,
  onToggle,
}: {
  projectId: string
  issue: IssueSummaryProps
  selected: boolean
  onToggle: () => void
}) {
  return (
    <Group
      wrap="nowrap"
      px="md"
      py="sm"
      gap="sm"
      align="flex-start"
      style={{
        borderTop: '1px solid var(--mantine-color-default-border)',
        cursor: 'pointer',
      }}
      onClick={() => router.visit(`/projects/${projectId}/issues/${issue.id}`)}
    >
      <Checkbox
        checked={selected}
        onChange={(e) => {
          e.stopPropagation()
          onToggle()
        }}
        onClick={(e) => e.stopPropagation()}
        mt={2}
        aria-label={`Select issue ${issue.title}`}
      />

      <Box mt={2}>
        <IssueStateIcon state={issue.state} />
      </Box>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text fw={600} size="sm" lineClamp={1}>
            {issue.title}
          </Text>
          <IssueTypeBadge issueType={issue.issue_type} />
        </Group>

        <Text c="dimmed" size="xs" mt={2}>
          #{issue.number} opened {timeAgo(issue.created_at)} by {issue.author_name || 'Unknown'}
        </Text>
      </Box>

      <Group gap="sm" wrap="nowrap" align="center">
        {issue.comment_count > 0 && (
          <Group gap={4} wrap="nowrap">
            <MessageSquare size={14} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              {issue.comment_count}
            </Text>
          </Group>
        )}
        {issue.assignee_name && (
          <Avatar size="sm" radius="xl" color={issue.assignee_type === 'agent' ? 'violet' : 'teal'}>
            {issue.assignee_name.charAt(0).toUpperCase()}
          </Avatar>
        )}
      </Group>
    </Group>
  )
}

function NewIssueForm({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { csrf_token } = usePage<{ csrf_token: string }>().props

  const { data, setData, post, processing, reset } = useForm({
    issue: { title: '', description: '', issue_type: 'task' },
    authenticity_token: csrf_token,
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(`/projects/${projectId}/issues`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        onClose()
      },
    })
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} p="lg" mt="md">
      <Stack gap="md">
        <TextInput
          label="Title"
          value={data.issue.title}
          onChange={(e) => setData('issue', { ...data.issue, title: e.target.value })}
          required
          autoFocus
        />

        <Textarea
          label="Description"
          autosize
          minRows={3}
          value={data.issue.description}
          onChange={(e) => setData('issue', { ...data.issue, description: e.target.value })}
        />

        <NativeSelect
          label="Type"
          value={data.issue.issue_type}
          onChange={(e) => setData('issue', { ...data.issue, issue_type: e.target.value })}
          data={[
            { value: 'task', label: 'Task' },
            { value: 'fix', label: 'Fix' },
            { value: 'enhancement', label: 'Enhancement' },
            { value: 'chore', label: 'Chore' },
            { value: 'question', label: 'Question' },
            { value: 'generic', label: 'Generic' },
          ]}
        />

        <Group>
          <Button type="submit" loading={processing}>
            Create Issue
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

type SortOption = 'newest' | 'oldest' | 'most_commented' | 'recently_updated'

export default function IssuesIndex() {
  const { project, issues, filter_state, not_closed_count, closed_count, ancestors } =
    usePage<IssueIndexPageProps>().props
  const { errors: pageErrors } = usePage<{ errors: Record<string, string[]> }>().props
  const hasPageErrors = pageErrors && Object.keys(pageErrors).some((key) => key.startsWith('issue.'))

  const [showNewForm, setShowNewForm] = useState(!!hasPageErrors)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [sortBy, setSortBy] = useState<SortOption>('newest')

  const filteredAndSorted = useMemo(() => {
    let result = issues
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter((i) => i.title.toLowerCase().includes(q))
    }
    if (sortBy === 'oldest') {
      result = [...result].reverse()
    } else if (sortBy === 'most_commented') {
      result = [...result].sort((a, b) => b.comment_count - a.comment_count)
    }
    return result
  }, [issues, searchQuery, sortBy])

  const allSelected = filteredAndSorted.length > 0 && filteredAndSorted.every((i) => selectedIds.has(i.id))

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredAndSorted.map((i) => i.id)))
    }
  }

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <>
      <Head title={`Issues - ${project.name}`} />

      <Container size="lg" py="md">
        <Stack gap="md">
          {/* Search bar + New Issue button */}
          <Group gap="sm">
            <TextInput
              placeholder="Search issues..."
              leftSection={<Search size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button onClick={() => setShowNewForm(true)}>New issue</Button>
          </Group>

          {showNewForm && (
            <NewIssueForm projectId={project.id} onClose={() => setShowNewForm(false)} />
          )}

          {/* Issue table */}
          <Paper p={0} withBorder>
            {/* Table header */}
            <Group
              px="md"
              py="sm"
              justify="space-between"
              wrap="nowrap"
              style={{
                backgroundColor: 'var(--mantine-color-gray-0)',
                borderRadius: 'var(--mantine-radius-lg) var(--mantine-radius-lg) 0 0',
              }}
            >
              <Group gap="md" wrap="nowrap">
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedIds.size > 0 && !allSelected}
                  onChange={toggleAll}
                  aria-label="Select all issues"
                />

                <UnstyledButton
                  onClick={() =>
                    router.visit(`/projects/${project.id}/issues?state=not_closed`, {
                      preserveState: true,
                    })
                  }
                >
                  <Group gap={4} wrap="nowrap">
                    <CircleDot size={16} />
                    <Text
                      size="sm"
                      fw={filter_state === 'not_closed' ? 700 : 400}
                      c={filter_state === 'not_closed' ? undefined : 'dimmed'}
                    >
                      {not_closed_count} Open
                    </Text>
                  </Group>
                </UnstyledButton>

                <UnstyledButton
                  onClick={() =>
                    router.visit(`/projects/${project.id}/issues?state=closed`, {
                      preserveState: true,
                    })
                  }
                >
                  <Group gap={4} wrap="nowrap">
                    <CheckCircle2 size={16} />
                    <Text
                      size="sm"
                      fw={filter_state === 'closed' ? 700 : 400}
                      c={filter_state === 'closed' ? undefined : 'dimmed'}
                    >
                      {closed_count} Closed
                    </Text>
                  </Group>
                </UnstyledButton>
              </Group>

              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <UnstyledButton>
                    <Group gap={4} wrap="nowrap">
                      <ArrowDownWideNarrow size={16} />
                      <Text size="sm" c="dimmed">
                        Sort
                      </Text>
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Sort by</Menu.Label>
                  <Menu.Item
                    onClick={() => setSortBy('newest')}
                    fw={sortBy === 'newest' ? 700 : undefined}
                  >
                    Newest
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => setSortBy('oldest')}
                    fw={sortBy === 'oldest' ? 700 : undefined}
                  >
                    Oldest
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => setSortBy('most_commented')}
                    fw={sortBy === 'most_commented' ? 700 : undefined}
                  >
                    Most commented
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>

            {/* Issue rows */}
            {filteredAndSorted.length > 0 ? (
              filteredAndSorted.map((issue) => (
                <IssueRow
                  key={issue.id}
                  projectId={project.id}
                  issue={issue}
                  selected={selectedIds.has(issue.id)}
                  onToggle={() => toggleOne(issue.id)}
                />
              ))
            ) : (
              <Box px="md" py="xl">
                <Text ta="center" c="dimmed" size="sm">
                  {searchQuery
                    ? 'No issues match your search.'
                    : `No ${filter_state === 'not_closed' ? 'open' : filter_state === 'all' ? '' : filter_state} issues yet.`}
                </Text>
              </Box>
            )}
          </Paper>
        </Stack>
      </Container>
    </>
  )
}
